// SPDX-License-Identifier: Apache-2.0

// CommsSnapshotButton — "remember this" affordance on every comms tool-card.
//
// Click → POST a `save_to_vault` tool call to the local MCP server over WS.
// The server-side `save_to_vault` tool is `ask-15m`, so the standard
// PermissionModal fires automatically before the write happens. We don't
// re-implement the permission flow here — we just dispatch and surface the
// outcome.
//
// The modal's "where this goes" hint is part of `argsSummary` (the
// PermissionModal renders argsSummary in its body), so the user sees the
// inbox/comms/<source>/ path explicitly before approving.
//
// Snapshot defaults per Iris's research:
//   - source 'slack' DM/private  → sensitivity 'private'
//   - source 'slack' public      → sensitivity 'internal'
//   - source 'mail'              → sensitivity 'private' (inbox)
// Caller computes the default and passes via `defaultSensitivity`. The user
// can override using the button's title-attribute hint (full override UI is
// out of scope for this stream — falls under the global PermissionModal,
// which can be extended in a later spawn).

import { Show, createSignal } from "solid-js";
import { getActiveWorkspaceIdHeader } from "../../lib/api";
import "./comms.css";

const MCP_WS_URL = "ws://localhost:5417/ws";

export type CommsSource = "slack" | "mail";
export type Sensitivity = "public" | "internal" | "private" | "secret";

interface Props {
  /** Source key — drives folder + tags. */
  source: CommsSource;
  /** Title for the saved note (also the markdown H1). */
  title: string;
  /** Filename slug (will be sanitised + date-prefixed by save_to_vault). */
  slug: string;
  /** Markdown body to save. */
  content: string;
  /** Vault-relative folder, defaults to `inbox/comms/<source>/`. */
  location?: string;
  /** Auto-applied sensitivity tier; user may override before approving. */
  defaultSensitivity: Sensitivity;
  /** Tag list — typically `["comms", source]`. */
  tags?: string[];
  /** Optional onSaved callback for tests / parent state. */
  onSaved?: (path: string) => void;
}

type ToolCallEnvelope = {
  jsonrpc: "2.0";
  id: number;
  result?: {
    content?: Array<{ type?: string; text?: string }>;
    saved?: string;
    absolutePath?: string;
  };
  error?: { code: number; message: string };
};

let nextReqId = 100_000;

export default function CommsSnapshotButton(props: Props) {
  const [state, setState] = createSignal<"idle" | "pending" | "saved" | "error">(
    "idle",
  );
  const [savedPath, setSavedPath] = createSignal<string>("");
  const [errorMsg, setErrorMsg] = createSignal<string>("");

  const click = async () => {
    if (state() === "pending" || state() === "saved") return;
    setState("pending");
    setErrorMsg("");

    const args = {
      title: props.title,
      content: props.content,
      filename: props.slug,
      location: props.location ?? `inbox/comms/${props.source}/`,
      sensitivity: props.defaultSensitivity,
      tags: props.tags ?? ["comms", props.source],
      type: "comms",
    };

    try {
      const result = await dispatchSaveToVault(args);
      const saved = String(result.saved ?? result.absolutePath ?? "saved");
      setSavedPath(saved);
      setState("saved");
      props.onSaved?.(saved);
    } catch (err) {
      setState("error");
      setErrorMsg((err as Error).message ?? "save failed");
      // Reset to idle after a beat so the user can retry.
      setTimeout(() => setState((s) => (s === "error" ? "idle" : s)), 4000);
    }
  };

  const label = () => {
    const s = state();
    if (s === "pending") return "saving…";
    if (s === "saved") return "remembered";
    if (s === "error") return "retry";
    return "remember this";
  };

  const title = () => {
    const where = props.location ?? `inbox/comms/${props.source}/`;
    if (state() === "saved" && savedPath()) return `saved → ${savedPath()}`;
    if (state() === "error") return `error: ${errorMsg() || "unknown"}`;
    return `save to ~/brain/${where} (sensitivity: ${props.defaultSensitivity})`;
  };

  return (
    <button
      type="button"
      class={`mv-comms-snapshot${state() === "saved" ? " mv-comms-snapshot--saved" : ""}`}
      data-comms-snapshot
      data-source={props.source}
      data-state={state()}
      onClick={() => void click()}
      disabled={state() === "pending"}
      title={title()}
    >
      <span class="mv-comms-snapshot__glyph" aria-hidden="true">
        {state() === "saved" ? "✓" : "★"}
      </span>
      <span>{label()}</span>
      <Show when={state() === "error"}>
        <span style={{ "margin-left": "6px", color: "#f87171" }}>!</span>
      </Show>
    </button>
  );
}

// ── Dispatch to MCP via WS ───────────────────────────────────────────
//
// The MCP server's WS transport speaks JSON-RPC 2.0; tools/call returns the
// MCP `content[]` envelope. The server's permission gate fires its own
// approval frame (the PermissionModal in ChatPanel handles those). We just
// wait for our request id to come back.
//
// Tests stub `globalThis.__tetherMcpDispatch` to bypass the WS — see
// e2e/comms-cards.spec.ts.

interface DispatchHook {
  (toolName: string, args: Record<string, unknown>): Promise<unknown>;
}

async function dispatchSaveToVault(args: Record<string, unknown>): Promise<{
  saved?: string;
  absolutePath?: string;
}> {
  const hook = (globalThis as unknown as { __tetherMcpDispatch?: DispatchHook })
    .__tetherMcpDispatch;
  if (hook) {
    const result = (await hook("save_to_vault", args)) as {
      saved?: string;
      absolutePath?: string;
    };
    return result ?? {};
  }

  return new Promise((resolve, reject) => {
    let ws: WebSocket;
    try {
      ws = new WebSocket(MCP_WS_URL);
    } catch (err) {
      reject(err as Error);
      return;
    }
    const id = nextReqId++;
    const timer = setTimeout(() => {
      try {
        ws.close();
      } catch {
        /* noop */
      }
      reject(new Error("save_to_vault timed out"));
    }, 30_000);

    ws.addEventListener("open", () => {
      // Bind the active workspace to this connection so the server resolves
      // tool dispatches (e.g. save_to_vault) against the correct vault.
      ws.send(
        JSON.stringify({
          type: "hello",
          workspaceId: getActiveWorkspaceIdHeader(),
        }),
      );
      ws.send(
        JSON.stringify({
          jsonrpc: "2.0",
          id,
          method: "tools/call",
          params: { name: "save_to_vault", arguments: args },
        }),
      );
    });
    ws.addEventListener("error", () => {
      clearTimeout(timer);
      reject(new Error("MCP unreachable"));
    });
    ws.addEventListener("message", (ev) => {
      let parsed: ToolCallEnvelope | { type?: string };
      try {
        parsed = JSON.parse(typeof ev.data === "string" ? ev.data : "");
      } catch {
        return;
      }
      if ("type" in parsed && parsed.type) return; // skip server hello / permission frames
      const r = parsed as ToolCallEnvelope;
      if (r.id !== id) return;
      clearTimeout(timer);
      try {
        ws.close();
      } catch {
        /* noop */
      }
      if (r.error) {
        reject(new Error(r.error.message));
        return;
      }
      // The `save_to_vault` handler returns `jsonResult({ saved, absolutePath, ... })`
      // which the WS transport wraps in `{ content: [{ type: 'text', text: '<json>' }] }`.
      const text = r.result?.content?.[0]?.text;
      if (text) {
        try {
          const inner = JSON.parse(text) as { saved?: string; absolutePath?: string };
          resolve(inner);
          return;
        } catch {
          resolve({});
          return;
        }
      }
      resolve({
        saved: r.result?.saved,
        absolutePath: r.result?.absolutePath,
      });
    });
  });
}
